import { createContext, useContext, useState } from "react";
import { checkAuth, getWhoami } from "../api/users";

interface UserContextValue {
  firstname: string;
  setFirstname: (value: string) => void;
  lastname: string;
  setLastname: (value: string) => void;
  emailUser: string;
  setEmailUser: (value: string) => void;
  role: string;
  setRole: (value: string) => void;
  rights: number[];
  setRights: (value: number[]) => void;
  departments: string[];
  setDepartments: (value: string[]) => void;
  idUser: number;
  setIdUser: (value: number) => void;
  typeRole: string;
  setTypeRole: (value: string) => void;
  social: boolean;
  setSocial: (value: boolean) => void;
  billingContact: boolean;
  setBillingContact: (value: boolean) => void;
  sc_UserToCustomerFile: CustomerFile[];
  setSc_UserToCustomerFile: (value: CustomerFile[]) => void;
  resetUserContext: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUserData: () => Promise<void>;
  checkAuthAndLoadData: () => Promise<void>;
}

interface CustomerFile {
  idCustomerFile: number;
  bulletin: boolean;
  paye: boolean;
}

const UserContext = createContext<UserContextValue | null>(null);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // States pour les données utilisateur
  const [idUser, setIdUser] = useState<number>(0);
  const [firstname, setFirstname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [emailUser, setEmailUser] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const [departments, setDepartments] = useState<string[]>([]);
  const [rights, setRights] = useState<number[]>([]);
  const [typeRole, setTypeRole] = useState<string>("");
  const [social, setSocial] = useState<boolean>(false);
  const [billingContact, setBillingContact] = useState<boolean>(true);
  const [sc_UserToCustomerFile, setSc_UserToCustomerFile] = useState<CustomerFile[]>([]);

  // States pour la gestion de l'authentification
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // Fonction pour récupérer les données utilisateur depuis l'API
  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const userData = await getWhoami();

      // Mise à jour des states avec les données de l'API
      setIdUser(userData.id);
      setRights(userData.rights);
      setFirstname(userData.firstName);
      setLastname(userData.lastName);
      setEmailUser(userData.email);
      setRole(userData.role);
      setTypeRole(userData.typeRole);
      setDepartments(userData.departments?.map((department: any) => department.name) || []);
      setSocial(userData.social || false);
      setBillingContact(userData.billingContact || false);
      
      // Gestion des dossiers clients si l'utilisateur est un client
      if (userData.typeRole === "Customer" && userData.sc_UserToCustomerFile) {
        setSc_UserToCustomerFile(userData.sc_UserToCustomerFile);
      } else {
        setSc_UserToCustomerFile([]);
      }

      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      resetUserContext();
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour vérifier l'authentification et charger les données
  const checkAuthAndLoadData = async () => {
    // Éviter les appels multiples
    if (hasInitialized) return;
    
    try {
      setIsLoading(true);
      setHasInitialized(true);
      
      // Si checkAuth() réussit, cela signifie que l'utilisateur est authentifié
      await checkAuth();
      // Si on arrive ici, l'utilisateur est authentifié, on peut charger ses données
      await fetchUserData();
      // On marque l'utilisateur comme authentifié
      setIsAuthenticated(true);
    } catch (error) {
      // En cas d'erreur (401, etc.), l'utilisateur n'est pas authentifié
      setIsAuthenticated(false);
      resetUserContext();
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction pour rafraîchir les données utilisateur
  const refreshUserData = async () => {
    // On peut rafraîchir les données même si pas encore authentifié (après connexion)
    await fetchUserData();
  };

  // Fonction wrapper pour sauvegarder les dossiers clients (pas de localStorage)
  const setSc_UserToCustomerFileWithStorage = (value: CustomerFile[]) => {
    setSc_UserToCustomerFile(value);
    // Note: Plus de localStorage, les données sont gérées côté serveur
  };

  const resetUserContext = () => {
    setIdUser(0);
    setFirstname("");
    setLastname("");
    setEmailUser("");
    setRole("");
    setDepartments([]);
    setTypeRole("");
    setRights([]);
    setSocial(false);
    setBillingContact(true);
    setSc_UserToCustomerFile([]);
    setIsAuthenticated(false);
  };

  return (
    <UserContext.Provider
      value={{
        firstname,
        setFirstname,
        lastname,
        setLastname,
        emailUser,
        setEmailUser,
        role,
        setRole,
        departments,
        setDepartments,
        idUser,
        setIdUser,
        typeRole,
        setTypeRole,
        social,
        setSocial,
        rights,
        setRights,
        billingContact,
        setBillingContact,
        sc_UserToCustomerFile,
        setSc_UserToCustomerFile: setSc_UserToCustomerFileWithStorage,
        resetUserContext,
        isLoading,
        isAuthenticated,
        refreshUserData,
        checkAuthAndLoadData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error("Le contexte doit être utilisé dans un UserProvider");
  }
  return context;
};
